import type { AppDataTableColumn } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import {
  AppBadge,
  AppBadgeForID,
  AppIconButton,
  AppMemberDisplay,
  AppSelectPopover,
  AppTooltip,
  type SelectOption,
} from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { mdiMicrosoftTeams } from '@mdi/js'
import type { OpportunityListResponse } from 'backend/types'
import { Copy, MessageSquare, Search, Tag } from 'lucide-react'
import { OPPORTUNITY_TYPE_IDS } from 'shared'
import { AnoMenuCell } from '@/components/AnoMenuCell'
import { COLOR_CATEGORY, getColor } from '@/lib/color-registry'
import { BASE_PATH } from '@/lib/env'
import { formatDate, toDateTime } from '@/lib/format-utils'
import { getTagGroupColor, parseTagName } from '@/lib/tag-utils'
import { cn } from '@/lib/utils'

/**
 * テキストを指定文字数で省略
 * dracaenaのtruncateTextフィルタに準拠（デフォルト25文字）
 */
function truncateText(text: string, maxLength = 25): string {
  if (text.length <= maxLength) return text
  return `${text.substring(0, maxLength)}...`
}

/**
 * 事業部名をツールチップ付きでレンダリング
 * 略称がある場合のみ、完全名をツールチップで表示
 * fullNameは#区切りをスペース区切りに変換して表示
 * 略称とフル名（整形後）が同じ場合は、冗長なツールチップを表示しない
 */
function renderDivisionWithTooltip(shortName: string | null | undefined, fullName: string | null | undefined) {
  const text = shortName || fullName || ''
  if (!text) return '-'

  if (shortName && fullName) {
    const formattedFullName = fullName.split('#').join(' ')

    if (shortName !== formattedFullName) {
      return (
        <AppTooltip content={formattedFullName}>
          <span className='cursor-default'>{text}</span>
        </AppTooltip>
      )
    }
  }

  return text
}

// オポチュニティ型（一覧レスポンスの要素）
type Opportunity = OpportunityListResponse['opportunities'][0]

/**
 * オポチュニティ一覧のカラム定義を生成
 * @param params カラム生成パラメータ
 * @returns カラム定義の配列
 */
export function createOpportunityListColumns(params: {
  /** 現在選択されているタブ（コピーボタンの有効/無効制御用） */
  selectedTab?: string
  /** アクションハンドラー */
  handlers?: {
    onCommentClick?: (opportunity: Opportunity) => void
    onTagEditClick?: (opportunity: Opportunity) => void
    onCopyClick?: (opportunity: Opportunity) => void
    onStatusChangeClick?: (opportunity: Opportunity, statusId: number) => void
    onTagClick?: (tagName: string) => void
  }
  /** ステータス一覧（ステータス変更用） */
  opportunityStatuses?: Array<{
    id: number
    name: string
    color: string
    is_system_managed: boolean
    is_completed: boolean
  }>
  /** 前回のチェック時刻（Unixタイムスタンプ秒） */
  lastCheckAt?: number
  /** 現在のユーザーのメンバーID */
  currentMemberId?: number | null
}): AppDataTableColumn<Opportunity>[] {
  const { selectedTab, handlers, opportunityStatuses, lastCheckAt, currentMemberId } = params
  /**
   * オポチュニティがNEWかどうかを判定
   * @param updatedAt 更新日時（Unixタイムスタンプ秒）
   * @param updatedBy 更新者のメンバーID
   * @returns NEWならtrue
   */
  const isNew = (updatedAt: number, updatedBy: number | null): boolean => {
    // 自分が更新したものは NEW を表示しない
    if (updatedBy === currentMemberId) return false
    // lastCheckAt が設定されていない場合は表示しない
    if (!lastCheckAt) return false
    // updatedAt は既にUnixタイムスタンプ秒なので直接比較
    return updatedAt > lastCheckAt
  }

  const handlePropertyClick = (row: Opportunity) => {
    const fromTabParam = selectedTab ? `&fromTab=${selectedTab}` : ''
    const url = `${BASE_PATH}/opportunities/${row.id}?tab=properties${fromTabParam}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const allColumns: AppDataTableColumn<Opportunity>[] = [
    {
      key: 'id',
      label: '管理ID',
      accessor: (row) => row.id,
      render: (row, value) => {
        const showNewBadge = isNew(row.updated_at, row.updated_by)
        return (
          <div className='relative inline-block'>
            <AppBadgeForID value={value as number} />
            {showNewBadge && (
              <span className='absolute -top-1 -right-3 bg-destructive text-destructive-foreground text-[8px] font-bold px-0.5 rounded leading-tight'>
                New
              </span>
            )}
          </div>
        )
      },
      cellClassName: 'min-w-[100px] text-center',
      headerClassName: 'min-w-[100px] text-center',
      required: true,
    },
    {
      key: 'title',
      label: 'タイトル',
      accessor: (row) => row.title,
      render: (row, value) => {
        return (
          <div className='flex flex-col gap-1'>
            <span>{value as string}</span>
            {row.tags && row.tags.length > 0 && (
              <div className='flex flex-wrap gap-1'>
                {row.tags.map((tag) => (
                  <AppBadge
                    key={tag.id}
                    color='none'
                    clickable
                    className={cn('text-xs', getTagGroupColor(parseTagName(tag.name).group))}
                    onClick={(e) => {
                      e.stopPropagation()
                      handlers?.onTagClick?.(tag.name)
                    }}
                  >
                    {tag.name}
                  </AppBadge>
                ))}
              </div>
            )}
          </div>
        )
      },
      cellClassName: 'min-w-[200px]',
      headerClassName: 'min-w-[200px]',
      required: true,
    },
    {
      key: 'opportunity_type_name',
      label: 'オポ種別',
      accessor: (row) => row.opportunity_type_name,
      render: (_row, value) => (
        <AppBadge outline className='whitespace-nowrap'>
          {(value as string) || '-'}
        </AppBadge>
      ),
      cellClassName: 'min-w-[100px] text-center',
      headerClassName: 'min-w-[100px] text-center',
    },
    {
      key: 'opportunity_status_name',
      label: 'ステータス',
      accessor: (row) => row.opportunity_status_name,
      render: (row, value) => {
        // ステータス一覧が取得できていない場合は変更不可でバッジのみ表示
        if (!opportunityStatuses) {
          return (
            <AppBadge
              color={getColor(COLOR_CATEGORY.OPPORTUNITY_STATUS, row.opportunity_status_id)}
              className='whitespace-nowrap'
            >
              {value as string}
            </AppBadge>
          )
        }

        const currentStatus = opportunityStatuses.find((s) => s.id === row.opportunity_status_id)
        const isSystemManaged = currentStatus?.is_system_managed || false

        return (
          <AppSelectPopover
            currentValue={String(row.opportunity_status_id)}
            options={opportunityStatuses
              .filter((s) => !s.is_system_managed || s.id === row.opportunity_status_id)
              .map(
                (s): SelectOption => ({
                  value: String(s.id),
                  label: s.name,
                  color: getColor(COLOR_CATEGORY.OPPORTUNITY_STATUS, s.id),
                }),
              )}
            onSelect={(value) => handlers?.onStatusChangeClick?.(row, Number(value))}
            disabled={isSystemManaged}
            triggerVariant='ghost'
            header='ステータスを変更'
            contentClassName='w-64'
          />
        )
      },
      cellClassName: 'min-w-[80px] text-center',
      headerClassName: 'min-w-[80px] text-center',
      active: true,
    },
    {
      key: 'opportunity_close_reason',
      label: 'クローズ理由',
      accessor: (row) => row.opportunity_close_reason,
      cellClassName: 'min-w-[80px] text-center',
      headerClassName: 'min-w-[80px] text-center',
      active: false,
    },
    {
      key: 'opportunity_additional_close_reason',
      label: 'クローズ理由(その他)',
      accessor: (row) => row.opportunity_additional_close_reason,
      render: (_row, value) => {
        const text = (value as string) || ''
        if (!text) return null

        return (
          <AppTooltip content={text}>
            <span className='cursor-default'>{truncateText(text)}</span>
          </AppTooltip>
        )
      },
      cellClassName: 'min-w-[120px]',
      headerClassName: 'min-w-[120px]',
      active: false,
    },
    {
      key: 'is_expansion',
      label: '新規案件',
      accessor: (row) => row.is_expansion,
      render: (_row, value) =>
        !(value as boolean) ? (
          <AppBadge color='destructive' className='text-xs'>
            新規
          </AppBadge>
        ) : null,
      sortable: false,
      cellClassName: 'min-w-[60px] text-center',
      headerClassName: 'min-w-[60px] text-center',
      active: true,
    },
    {
      key: 'created_by_name',
      label: '起票者',
      accessor: (row) => row.created_by_name,
      render: (row) => <AppMemberDisplay employeeNumber={row.created_by_employee_number} name={row.created_by_name} />,
      cellClassName: 'min-w-[150px]',
      headerClassName: 'min-w-[150px]',
      required: true,
    },
    {
      key: 'created_by_division_name',
      label: '起票者の所属',
      accessor: (row) => row.short_created_by_division_name || row.created_by_division_name,
      render: (row) => renderDivisionWithTooltip(row.short_created_by_division_name, row.created_by_division_name),
      cellClassName: 'min-w-[120px]',
      headerClassName: 'min-w-[120px]',
      active: true,
    },
    {
      key: 'salesforce_project_name',
      label: 'SF案件名',
      accessor: (row) => row.salesforce_project_name,
      cellClassName: 'min-w-[120px]',
      headerClassName: 'min-w-[120px]',
      required: true,
    },
    {
      key: 'ano',
      label: 'ANO',
      accessor: (row) => row.ano,
      render: (row) => <AnoMenuCell ano={row.ano} salesforceProjectId={row.salesforce_project_id} pksId={row.pks_id} />,
      cellClassName: 'min-w-[100px] text-center',
      headerClassName: 'min-w-[100px] text-center',
      required: true,
    },
    {
      key: 'description',
      label: '詳細',
      accessor: (row) => row.description,
      render: (_row, value) => {
        const text = (value as string) || ''
        if (!text) return '-'

        return (
          <AppTooltip content={<p className='whitespace-pre-wrap'>{text}</p>} className='max-w-md'>
            <span className='cursor-default'>{truncateText(text)}</span>
          </AppTooltip>
        )
      },
      sortable: false,
      cellClassName: 'min-w-[100px]',
      headerClassName: 'min-w-[100px]',
      active: true,
    },
    {
      key: 'customer_name',
      label: '取引先',
      accessor: (row) => row.customer_name,
      cellClassName: 'min-w-[120px]',
      headerClassName: 'min-w-[120px]',
      active: false,
    },
    {
      key: 'contact_name',
      label: '取引先責任者',
      accessor: (row) => row.contact_name,
      sortable: false,
      cellClassName: 'min-w-[70px]',
      headerClassName: 'min-w-[70px]',
      active: false,
    },
    {
      key: 'sales_recording_group_name',
      label: 'サービスジョブ名',
      accessor: (row) => row.sales_recording_group_name,
      cellClassName: 'min-w-[100px]',
      headerClassName: 'min-w-[100px]',
      active: false,
    },
    {
      key: 'start_on',
      label: '案件の開始予定日',
      accessor: (row) => row.start_on,
      render: (_row, value) => (value ? formatDate(value as string, 'date') : '-'),
      cellClassName: 'min-w-[100px]',
      headerClassName: 'min-w-[100px]',
      active: false,
    },
    {
      key: 'information_source',
      label: 'オポチュニティの情報源',
      accessor: (row) => row.information_source,
      sortable: false,
      cellClassName: 'min-w-[120px]',
      headerClassName: 'min-w-[120px]',
      active: false,
    },
    {
      key: 'vertical_division_name',
      label: '縦事業部',
      accessor: (row) => row.short_vertical_division_name || row.vertical_division_name,
      render: (row) => renderDivisionWithTooltip(row.short_vertical_division_name, row.vertical_division_name),
      cellClassName: 'min-w-[120px]',
      headerClassName: 'min-w-[120px]',
      active: false,
    },
    {
      key: 'lateral_division_name',
      label: '横事業部',
      accessor: (row) => row.short_lateral_division_name || row.lateral_division_name,
      render: (row) => renderDivisionWithTooltip(row.short_lateral_division_name, row.lateral_division_name),
      cellClassName: 'min-w-[120px]',
      headerClassName: 'min-w-[120px]',
      active: false,
    },
    {
      key: 'comment_count',
      label: 'コメント数',
      accessor: (row) => row.comment_count,
      cellClassName: 'min-w-[70px] text-center',
      headerClassName: 'min-w-[70px] text-center',
      active: false,
    },
    {
      key: 'task_count',
      label: 'タスク数',
      accessor: (row) => row.task_count,
      cellClassName: 'min-w-[60px] text-center',
      headerClassName: 'min-w-[60px] text-center',
      active: false,
    },
    {
      key: 'teams',
      label: 'Teams',
      accessor: (row) => row.chat_id,
      render: (_row, value) => {
        if (!value) return null
        return (
          <a
            href={`https://teams.microsoft.com/l/chat/${value}/conversations`}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center justify-center text-primary hover:text-primary/80'
            aria-label='Teams'
          >
            <svg className='h-4 w-4' viewBox='0 0 24 24' fill='currentColor'>
              <title>Teams</title>
              <path d={mdiMicrosoftTeams} />
            </svg>
          </a>
        )
      },
      sortable: false,
      cellClassName: 'min-w-[80px] text-center',
      headerClassName: 'min-w-[80px] text-center',
      active: true,
    },
    {
      key: 'disabled_at',
      label: 'インセンティブ',
      accessor: (row) => row.disabled_at,
      render: (row, value) => {
        if (!value) return null
        const isSalesOpportunity = row.opportunity_type_id === OPPORTUNITY_TYPE_IDS.SALES
        const tooltipMessage = isSalesOpportunity
          ? '営業オポはインセンティブ対象外です'
          : 'オポチュニティのインセンティブ対象期限が切れています'
        return (
          <AppTooltip content={tooltipMessage}>
            <AppBadge color='gray' className='cursor-default'>
              対象外
            </AppBadge>
          </AppTooltip>
        )
      },
      cellClassName: 'min-w-[100px] text-center',
      headerClassName: 'min-w-[100px] text-center',
      active: true,
    },
    {
      key: 'updated_at',
      label: '更新日時',
      accessor: (row) => row.updated_at,
      render: (_row, value) => {
        if (!value) return '-'
        return (
          <AppTooltip content={toDateTime(value as number)?.toFormat('yyyy/MM/dd HH:mm:ss') ?? '-'}>
            <span className='cursor-help'>{formatDate(value as number, 'relative')}</span>
          </AppTooltip>
        )
      },
      cellClassName: 'min-w-[70px]',
      headerClassName: 'min-w-[70px]',
      active: false,
    },
    {
      key: 'created_at',
      label: '作成日時',
      accessor: (row) => row.created_at,
      render: (_row, value) => {
        if (!value) return '-'
        return (
          <AppTooltip content={toDateTime(value as number)?.toFormat('yyyy/MM/dd HH:mm:ss') ?? '-'}>
            <span className='cursor-help'>{formatDate(value as number, 'relative')}</span>
          </AppTooltip>
        )
      },
      cellClassName: 'min-w-[70px]',
      headerClassName: 'min-w-[70px]',
      active: false,
    },
    {
      key: 'action',
      label: 'Action',
      accessor: () => null,
      render: (row) => (
        <div className='flex items-center justify-center'>
          <AppIconButton onClick={() => handlePropertyClick(row)} description='プロパティを開く'>
            <Search />
          </AppIconButton>

          <AppIconButton onClick={() => handlers?.onCommentClick?.(row)} description='クイックコメント'>
            <MessageSquare />
          </AppIconButton>

          <AppIconButton onClick={() => handlers?.onTagEditClick?.(row)} description='タグの編集'>
            <Tag />
          </AppIconButton>

          <AppIconButton
            onClick={() => handlers?.onCopyClick?.(row)}
            size='sm'
            variant='ghost'
            description='既存オポチュニティから新規作成'
            disabled={!(selectedTab === 'captain' || selectedTab === 'sales')}
          >
            <Copy />
          </AppIconButton>
        </div>
      ),
      sortable: false,
      cellClassName: 'min-w-[150px] text-center',
      headerClassName: 'min-w-[150px] text-center',
      required: true,
    },
  ]

  return allColumns
}
